import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import API_BASE from '../config';
import { formatNumber } from '../lib/phoneFormat';

/**
 * Second half of add-to-contacts: attach an already-saved number to a
 * DIFFERENT existing contact (e.g. a second line that belongs on someone's
 * existing card), rather than creating a duplicate person. This is the
 * affordance that replaces the ➕ "create new" button once
 * isNumberSaved() is true.
 *
 * It reuses the same POST /contacts/{id}/numbers endpoint that Contacts.js's
 * "+ Add another number" edit form exercises — no new backend behavior.
 *
 * Selection: a lightweight inline picker over the line-scoped saved-contacts
 * list (the same `contacts` array CallLog/Messages already hold via
 * useLineContacts, passed in as a prop), filtered by name/number. We do NOT
 * refetch /contacts here, which also gives us free in-memory collision
 * detection: if the number is already attached to the picked contact we show a
 * brief confirm rather than silently duplicating it.
 *
 * Props:
 *   selectedNumber  line these contacts are scoped to (used for ?line=)
 *   authToken       bearer token for the POST
 *   number          the already-saved number being attached
 *   contacts        the line-scoped contacts array (from useLineContacts)
 *   onAdded         callback invoked after a successful POST (caller refetches)
 */
export default function AddToExistingContact({
  selectedNumber, authToken, number, contacts, onAdded,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState(null); // picked contact that already owns `number`
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const target = formatNumber(number || '');

  // Does this contact (primary or any extra number) already carry `target`?
  const hasNumber = (c) => {
    if (!c) return false;
    const nums = [
      formatNumber(c.number),
      ...(c.numbers || []).map((n) => formatNumber(n && n.number)),
    ];
    return nums.includes(target);
  };

  const filtered = (contacts || []).filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const name = (c.name || '').toLowerCase();
    const num = (c.number || '').toLowerCase();
    return name.includes(q) || num.includes(q);
  });

  const addTo = async (c) => {
    setBusy(true);
    setError('');
    setConfirmId(null);
    try {
      const qs = selectedNumber ? `?line=${encodeURIComponent(selectedNumber)}` : '';
      const res = await fetch(`${API_BASE}/contacts/${encodeURIComponent(c.id)}/numbers${qs}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ number: target, label: 'other' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data && data.error ? data.error : `Add failed (${res.status})`);
      setOpen(false);
      setQuery('');
      if (typeof onAdded === 'function') onAdded();
    } catch (e) {
      setError(e && e.message ? e.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const pick = (c) => {
    if (hasNumber(c)) {
      // Already on this specific contact — family-shared line / wrong-record
      // case. Confirm intent rather than silently duplicating the row.
      setConfirmId((prev) => (prev === c.id ? null : c.id));
      return;
    }
    addTo(c);
  };

  // Keep the popover inside the viewport: it is right-anchored to the icon,
  // so on a narrow layout its left edge can land off-screen (truva#186).
  const popRef = useRef(null);
  const [shiftX, setShiftX] = useState(0);
  useLayoutEffect(() => {
    if (!open || !popRef.current) return;
    const r = popRef.current.getBoundingClientRect();
    const cur = shiftX;
    const left = r.left - cur;
    const right = r.right - cur;
    const vw = window.innerWidth;
    let next = 0;
    if (left < 8) next = 8 - left;
    else if (right > vw - 8) next = vw - 8 - right;
    if (next !== cur) setShiftX(next);
  }, [open, shiftX]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setError(''); }}
        title="Add this number to a contact"
        aria-label="Add this number to an existing contact"
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          padding: '6px 8px',
          borderRadius: 8,
          color: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3.4 19.5c.6-3 2.8-4.7 5.6-4.7s5 1.7 5.6 4.7" />
          <path d="M17.5 9v7" />
          <path d="M14 12.5h7" />
        </svg>
      </button>

      {open && (
        <div
          ref={popRef}
          style={{
            transform: shiftX ? `translateX(${shiftX}px)` : undefined,
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 40,
            width: 260,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--truva-surface)',
            border: '1px solid var(--truva-border)',
            borderRadius: 10,
            boxShadow: 'var(--truva-elevation-e1)',
            padding: 8,
          }}
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '6px 8px',
              borderRadius: 6,
              border: '1px solid var(--truva-border)',
              marginBottom: 6,
              fontSize: 15,
              background: 'transparent',
              color: 'inherit',
            }}
          />

          {filtered.length === 0 ? (
            <div style={{ fontSize: 14, opacity: 0.6, padding: '8px 4px' }}>
              No contacts
            </div>
          ) : filtered.map((c) => (
            <div key={c.id}>
              <button
                type="button"
                onClick={() => pick(c)}
                disabled={busy}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 4px',
                  border: 'none',
                  background: 'none',
                  cursor: busy ? 'wait' : 'pointer',
                  color: 'inherit',
                  fontSize: 15,
                  borderRadius: 6,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {c.name}
                  {hasNumber(c) && (
                    <span style={{ fontSize: 12, opacity: 0.6, fontWeight: 400, marginLeft: 6 }}>already on this card</span>
                  )}
                </div>
                <div style={{ fontSize: 13, opacity: 0.7 }}>{c.number}</div>
              </button>

              {confirmId === c.id && (
                <div
                  style={{
                    margin: '2px 4px 8px',
                    padding: '8px',
                    borderRadius: 8,
                    background: 'var(--truva-border)',
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                >
                  <div>This number is already on {c.name || 'this contact'}. Add it here anyway?</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button
                      type="button"
                      onClick={() => addTo(c)}
                      disabled={busy}
                      style={{
                        border: 'none',
                        background: 'var(--truva-action-blue)',
                        color: 'var(--truva-on-action)',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 14,
                        cursor: busy ? 'wait' : 'pointer',
                      }}
                    >
                      {busy ? 'Adding…' : 'Add anyway'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(null)}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: 'inherit',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {error && (
            <div style={{ fontSize: 14, color: 'var(--truva-danger-text)', padding: '4px 2px 0' }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
